import React, { useCallback, useEffect, useState } from 'react'
import { Card, ListGroup, Badge, Form, Button } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import apiService from 'Service'
import LoadingSpinner from 'Components/LoadingSpinner'
import { Link } from 'react-router-dom'
import { FISCAL_CALENDAR_COMPANY } from 'Constants/Routes'
import { getFiscalObligationLabel } from 'Utilities/fiscalObligations'
import { formatCivilDate, todayCivil } from 'Utilities/dateTime'

const FiscalCalendarWidget = ({ compact = true, maxItems = 6 }) => {
  const { t } = useTranslation()
  const [entries, setEntries] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [rifDigit, setRifDigit] = useState(null)

  const load = useCallback(() => {
    setLoading(true)
    apiService.FiscalCalendar.getCompanySchedule(Number(todayCivil().slice(0, 4)))
      .then(data => {
        const list = Array.isArray(data) ? data : []
        setEntries(compact ? list.slice(0, maxItems) : list)
        setRifDigit(list[0]?.RifLastDigit ?? null)
        setError(false)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }, [compact, maxItems])

  useEffect(() => { load() }, [load])

  const toggleComplete = (entry) => {
    const action = entry.IsCompleted
      ? apiService.FiscalCalendar.unmarkCompleted(entry.EntryId)
      : apiService.FiscalCalendar.markCompleted(entry.EntryId)
    action.then(() => load())
  }

  return (
    <Card className="h-100 dashboard-panel dashboard-calendar">
      <Card.Header className="dashboard-panel__header">
        <div>
          <span className="dashboard-panel__eyebrow">
            {t('dashboard_upcoming_obligations', { defaultValue: 'Próximas obligaciones' })}
          </span>
          <h2>{t('fiscal_calendar')}</h2>
        </div>
        <div>
          {rifDigit != null && (
            <Badge variant="light" className="text-dark mr-2">RIF ·{rifDigit}</Badge>
          )}
          <Link to={FISCAL_CALENDAR_COMPANY} className="dashboard-panel__link">
            {t('view_all') || 'Ver todo'} <i className="fas fa-arrow-right" aria-hidden="true" />
          </Link>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {loading && <LoadingSpinner />}
        {!loading && error && (
          <p className="text-muted p-3 mb-0">{t('error_loading_data')}</p>
        )}
        {!loading && !error && entries.length === 0 && (
          <p className="text-muted p-3 mb-0">{t('no_upcoming_obligations')}</p>
        )}
        {!loading && !error && entries.length > 0 && (
          <ListGroup variant="flush">
            {entries.map(entry => (
              <ListGroup.Item key={entry.EntryId} className="dashboard-calendar__item">
                <Form.Check
                  type="checkbox"
                  checked={entry.IsCompleted}
                  onChange={() => toggleComplete(entry)}
                  title={t('mark_completed') || 'Marcar cumplida'}
                />
                <div className="flex-grow-1">
                  <Badge variant={entry.IsCompleted ? 'success' : 'secondary'} className="mr-2">
                    {getFiscalObligationLabel(entry.ObligationType, t)}
                  </Badge>
                  <span className={entry.IsCompleted ? 'text-muted text-decoration-line-through' : ''}>
                    {String(entry.Month).padStart(2, '0')}/{entry.Year}
                    {' · '}{t('due_day') || 'Vence'} {formatCivilDate(entry.DueDate) || entry.DueDay}
                  </span>
                  {entry.DaysUntilDue >= 0 && entry.DaysUntilDue <= 7 && !entry.IsCompleted && (
                    <Badge variant="warning" className="text-dark ml-2">
                      {entry.DaysUntilDue === 0 ? t('due_today') || 'Hoy' : `${entry.DaysUntilDue}d`}
                    </Badge>
                  )}
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        )}
      </Card.Body>
    </Card>
  )
}

export default FiscalCalendarWidget
